import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Lock, ArrowRight, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  
  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid reset link. Please request a new password reset.');
        setIsVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
        setUserEmail(response.data.email || '');
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired reset link. Please request a new password reset.');
        setTokenValid(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setResetSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 stars-bg opacity-40 pointer-events-none" />
      <div className="fixed inset-0 nebula-bg pointer-events-none" />
      
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-10">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <span className="font-['Fraunces'] text-4xl font-semibold text-[#282622]">Nakshatra</span>
        </Link>
        
        {/* Reset Password Card */}
        <div className="glass rounded-3xl p-8 cosmic-card">
          {isVerifying ? (
            /* Loading State */
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-[#C84B32] animate-spin mx-auto mb-4" />
              <p className="text-[#6B6560]">Verifying reset link...</p>
            </div>
          ) : resetSuccess ? (
            /* Success State */
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-['Fraunces'] text-2xl text-[#282622] mb-3">
                Password Reset Complete
              </h2>
              <p className="text-[#6B6560] text-sm mb-6">
                Your password has been successfully reset.<br />
                You can now login with your new password.
              </p>
              <Button
                type="button"
                onClick={() => navigate('/login')}
                data-testid="go-to-login-btn"
                className="w-full bg-gradient-to-r from-[#C84B32] to-[#E85D43] text-white hover:from-[#E85D43] hover:to-[#F5B942] h-14 rounded-xl font-semibold"
              >
                Go to Login
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          ) : !tokenValid ? (
            /* Invalid Token State */
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="font-['Fraunces'] text-2xl text-[#282622] mb-3">
                Invalid Reset Link
              </h2>
              <p className="text-[#6B6560] text-sm mb-6">
                {error || 'This password reset link is invalid or has expired.'}
              </p>
              <Button
                type="button"
                onClick={() => navigate('/login')}
                data-testid="back-to-login-btn"
                className="w-full bg-gradient-to-r from-[#C84B32] to-[#E85D43] text-white hover:from-[#E85D43] hover:to-[#F5B942] h-14 rounded-xl font-semibold"
              >
                Back to Login
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          ) : (
            /* Reset Password Form */
            <>
              <div className="text-center mb-8">
                <h2 className="font-['Fraunces'] text-3xl text-[#282622] mb-2">
                  Create New Password
                </h2>
                {userEmail && (
                  <p className="text-[#6B6560] text-sm">
                    for <span className="text-[#C84B32]">{userEmail}</span>
                  </p>
                )}
              </div>
              
              {error && (
                <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 text-sm flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  {error}
                </div>
              )}
              
              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#6B6560] text-sm">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B6560]" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter new password (min 6 chars)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="new-password-input"
                      className="pl-12 bg-[#FFF9EF] border-[#282622]/10 text-[#282622] focus:border-[#C84B32] focus:ring-[#C84B32]/30 h-14 rounded-xl"
                      required
                      minLength={6}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[#6B6560] text-sm">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B6560]" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      data-testid="confirm-password-input"
                      className="pl-12 bg-[#FFF9EF] border-[#282622]/10 text-[#282622] focus:border-[#C84B32] focus:ring-[#C84B32]/30 h-14 rounded-xl"
                      required
                      minLength={6}
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={isLoading}
                  data-testid="reset-password-btn"
                  className="w-full bg-gradient-to-r from-[#C84B32] to-[#E85D43] text-white hover:from-[#E85D43] hover:to-[#F5B942] h-14 rounded-xl font-semibold text-lg gold-glow"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Reset Password
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
        
        {/* Back link */}
        <p className="text-center text-[#6B6560]/60 text-sm mt-6">
          Remember your password?{' '}
          <Link to="/login" className="text-[#C84B32] hover:underline">Sign in</Link>
        </p>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
